import { memo, ReactNode, useEffect, useRef, useState } from 'react';

import { Entity, stringifyEntityRef } from '@backstage/catalog-model';

import Autocomplete from '@mui/material/Autocomplete';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { useGetDaasEntities } from '../../../../hooks';
import { DAAS_APPLICATION_SET_TYPE } from '../../../../types';
import {
  ScaffolderFieldError,
  ScaffolderFormPaper,
  ScaffolderInputHeader,
} from '../../../components';
import {
  APPLICATION_DESCRIPTION_INFO,
  APPLICATION_NAME_INFO,
} from '../infoDoc';
import { useStyles } from '../styles';
import { DaasApplicationSetInputData } from '../types';
import { convertEntityToApplicationSetInput } from '../utils';

const DESCRIPTION_MAX_LENGTH = 250;

export interface ApplicationSetEditBasicInfoValue {
  applicationSetName: string;
  description: string;
  applicationSetRef?: string;
}

export interface ApplicationSetEditBasicInfoSectionProps {
  value: ApplicationSetEditBasicInfoValue;
  onChange: (value: ApplicationSetEditBasicInfoValue) => void;
  onApplicationSetEntitySelect: (
    data: Partial<DaasApplicationSetInputData>,
  ) => void;
  isFormAlreadyPopulated?: boolean;
  errors?: string[];
}

const findApplicationSetEntity = (
  entities: Entity[],
  applicationSetRef: string | undefined,
): Entity | null => {
  if (applicationSetRef) {
    return (
      entities.find(e => stringifyEntityRef(e) === applicationSetRef) ?? null
    );
  }
  return null;
};

const ApplicationSetEditBasicInfoSectionInner = ({
  value,
  onChange,
  onApplicationSetEntitySelect,
  isFormAlreadyPopulated = false,
  errors = [],
}: ApplicationSetEditBasicInfoSectionProps) => {
  const { classes } = useStyles();
  const { value: appSetEntities = [], loading: appSetsLoading } =
    useGetDaasEntities(DAAS_APPLICATION_SET_TYPE, false, true);

  const selectedEntity =
    value.applicationSetName || value.applicationSetRef
      ? findApplicationSetEntity(appSetEntities, value.applicationSetRef)
      : null;

  const [inaccessibleName, setInaccessibleName] = useState<string | null>(null);
  const hasAutoPrefilled = useRef(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  // Stable refs — keeps the effect deps free of the callbacks without stale
  // closures. Always read .current inside callbacks instead of closing over the
  // prop directly.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onApplicationSetEntitySelectRef = useRef(onApplicationSetEntitySelect);
  onApplicationSetEntitySelectRef.current = onApplicationSetEntitySelect;

  const notAccessible =
    Boolean(inaccessibleName) ||
    (errors.length > 0 && !value.applicationSetName);

  useEffect(() => {
    if (notAccessible) {
      sectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [notAccessible]);

  // Auto-prefill when arriving via URL with a pre-set ApplicationSet ref.
  // Skipped once the form has been populated (back-navigation or field edits)
  // to prevent overwriting user changes.
  useEffect(() => {
    if (
      (!value.applicationSetName && !value.applicationSetRef) ||
      appSetsLoading
    )
      return;
    const entity = findApplicationSetEntity(
      appSetEntities,
      value.applicationSetRef,
    );
    if (!entity) {
      setInaccessibleName(value.applicationSetName);
      return;
    }
    setInaccessibleName(null);
    if (hasAutoPrefilled.current || isFormAlreadyPopulated) return;
    hasAutoPrefilled.current = true;
    const fullData = convertEntityToApplicationSetInput(entity);
    onChangeRef.current({
      applicationSetName:
        fullData.applicationSetName || value.applicationSetName,
      description: fullData.description || '',
      applicationSetRef: stringifyEntityRef(entity),
    });
    onApplicationSetEntitySelectRef.current(fullData);
  }, [
    value.applicationSetName,
    value.applicationSetRef,
    appSetEntities,
    appSetsLoading,
    isFormAlreadyPopulated,
  ]); // callbacks excluded via refs

  const handleEntitySelect = (_: unknown, entity: Entity) => {
    setInaccessibleName(null);
    hasAutoPrefilled.current = true;
    const fullData = convertEntityToApplicationSetInput(entity);
    onChangeRef.current({
      applicationSetName: fullData.applicationSetName || entity.metadata.name,
      description: fullData.description || '',
      applicationSetRef: stringifyEntityRef(entity),
    });
    onApplicationSetEntitySelectRef.current(fullData);
  };

  let appSetHelperText: ReactNode;
  if (inaccessibleName) {
    appSetHelperText = (
      <ScaffolderFieldError
        message={`You do not have permission to edit ApplicationSet "${inaccessibleName}". Select a different ApplicationSet.`}
      />
    );
  } else if (notAccessible) {
    appSetHelperText = (
      <ScaffolderFieldError message="ApplicationSet is required" />
    );
  }

  return (
    <Box>
      <ScaffolderFormPaper>
        <ScaffolderInputHeader
          title="ApplicationSet configuration"
          description="Select an ApplicationSet to edit its configuration."
          variant="h5"
          divider
        />
        <Backdrop open={appSetsLoading} className={classes.loadingProgress}>
          <CircularProgress size={48} />
        </Backdrop>
        <div ref={sectionRef}>
          <Box className={classes.inputContainer}>
            <ScaffolderInputHeader
              title="ApplicationSet to edit"
              description="Select the ApplicationSet you want to edit."
              infoDoc={APPLICATION_NAME_INFO}
            />
            <Autocomplete
              size="small"
              // `disableClearable` narrows the value type to a non-null Entity,
              // but nothing is selected until the user picks an ApplicationSet.
              value={selectedEntity as Entity}
              loading={appSetsLoading}
              onChange={handleEntitySelect}
              options={appSetEntities}
              disableClearable
              getOptionLabel={option =>
                option.metadata.title || option.metadata.name
              }
              isOptionEqualToValue={(option, selected) =>
                stringifyEntityRef(option) === stringifyEntityRef(selected)
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="ApplicationSet"
                  variant="outlined"
                  margin="none"
                  required
                  error={notAccessible}
                  helperText={appSetHelperText}
                />
              )}
              renderOption={({ key, ...optionProps }, option) => (
                <li key={key} {...optionProps}>
                  <Box>
                    <Typography variant="body2" noWrap>
                      {option.metadata.title || option.metadata.name}
                    </Typography>
                    {option.metadata.title && (
                      <Typography
                        variant="caption"
                        noWrap
                        color="text.secondary"
                      >
                        {option.metadata.name}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              noOptionsText={
                appSetsLoading
                  ? 'Loading ApplicationSets...'
                  : 'No accessible ApplicationSets found'
              }
              loadingText="Loading ApplicationSets..."
            />
          </Box>
        </div>
        <Box className={classes.inputContainer}>
          <ScaffolderInputHeader
            title="Description"
            description="Specify a description of the ApplicationSet."
            infoDoc={APPLICATION_DESCRIPTION_INFO}
          />
          <TextField
            label="Description"
            value={value.description}
            onChange={e =>
              onChangeRef.current({ ...value, description: e.target.value })
            }
            variant="outlined"
            size="small"
            fullWidth
            multiline
            minRows={3}
            slotProps={{
              htmlInput: { maxLength: DESCRIPTION_MAX_LENGTH },
            }}
            helperText="Optional. Up to 250 characters."
          />
        </Box>
      </ScaffolderFormPaper>
    </Box>
  );
};

export const ApplicationSetEditBasicInfoSection = memo(
  ApplicationSetEditBasicInfoSectionInner,
);
